import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConnectionStatus } from '../types/connections';
import ExecutionDateModal from './ExecutionDateModal';
import { Pipeline } from '../types/pipelines';
import { apiFetch } from '../services/api';

const PipelineManager: React.FC = () => {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDateModal, setShowDateModal] = useState(false);
  const [pendingPipelineId, setPendingPipelineId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    loadConnectionStatus();
    loadPipelines();
  }, []);

  const loadConnectionStatus = async () => {
    try {
      const res = await apiFetch('/api/connections/status');
      if (res.ok) {
        setConnectionStatus(await res.json());
      }
    } catch (error) {
      console.error('Failed to load connection status:', error);
      // Error is already handled by apiFetch (redirect on 401)
    }
  };

  const loadPipelines = async () => {
    try {
      const res = await apiFetch(`/api/pipelines?t=${Date.now()}`);
      if (res.ok) {
        setPipelines(await res.json());
      }
    } catch (error) {
      console.error('Failed to load pipelines:', error);
      // Error is already handled by apiFetch (redirect on 401)
    } finally {
      setLoading(false);
    }
  };

  const openCreatePage = (type: Pipeline['type']) => {
    navigate(`/pipelines/new/${type}`);
  };

  const openEditPage = (pipeline: Pipeline) => {
    navigate(`/pipelines/${pipeline.id}/edit`);
  };

  const deletePipeline = (e: React.MouseEvent, pipelineId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDeleteId(pipelineId);
  };

  const executeDelete = async () => {
    if (!confirmDeleteId) return;

    const pipelineId = confirmDeleteId;
    setDeleteError(null);

    try {
      const res = await apiFetch(`/api/pipelines/${pipelineId}`, {
        method: 'DELETE'
      });

      console.log('[DELETE] Response status:', res.status);

      if (res.ok) {
        setConfirmDeleteId(null);
        await loadPipelines();
      } else {
        const text = await res.text();
        console.error('Failed to delete pipeline:', res.status, text);
        setDeleteError(`Failed to delete pipeline. Status: ${res.status}. Error: ${text}`);
      }
    } catch (error: any) {
      console.error('Failed to delete pipeline (exception):', error);
      if (error.message !== 'Unauthorized') {
        setDeleteError(`Failed to delete pipeline: ${error.message}`);
      }
      // 401 is already handled by apiFetch (redirect on 401)
    }
  };

  const cancelDelete = () => {
    setConfirmDeleteId(null);
    setDeleteError(null);
  };

  const runPipeline = (pipelineId: string) => {
    // Show date selection modal first
    setPendingPipelineId(pipelineId);
    setShowDateModal(true);
  };

  const handleDateConfirm = async (startDate: string, endDate: string) => {
    if (!pendingPipelineId) return;

    try {
      // Create execution with date parameters (as strings in YYYY-MM-DD format)
      const executionData = {
        pipelineId: pendingPipelineId,
        triggeredBy: 'manual' as const,
        parameters: {
          startDate: startDate,
          endDate: endDate
        }
      };

      const response = await fetch('/api/executions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('id_token')}`
        },
        body: JSON.stringify(executionData)
      });

      if (!response.ok) {
        throw new Error('Failed to create execution');
      }

      // Close modal and reload pipelines
      setShowDateModal(false);
      setPendingPipelineId(null);
      await loadPipelines();
    } catch (error) {
      console.error('Error creating execution:', error);
    }
  };

  const getPipelineRequirements = (type: Pipeline['type']) => {
    switch (type) {
      case 'ga4-sessionization':
        return ['GA4 raw export table'];
      case 'ga4-flattener':
        return ['GA4 raw export table', 'GA4 property'];
      case 'marketing-attribution':
        return ['GA4 raw export table', 'GA4 property', 'Google Ads cost data', 'Meta Ads cost data', 'CRM data'];
      case 'google-ads-cost-loader':
        return ['Google Ads Connection', 'GBQ connection to put the data'];
      default:
        return [];
    }
  };

  const checkRequirementsMet = (type: Pipeline['type']) => {
    if (!connectionStatus) return false;

    const hasGBQ = connectionStatus.gbq && connectionStatus.gbq.length > 0;
    const hasGA4 = connectionStatus.ga4 && connectionStatus.ga4.length > 0;
    const hasGads = !!(connectionStatus.gads && connectionStatus.gads.length > 0);

    switch (type) {
      case 'ga4-sessionization':
        return hasGBQ;
      case 'ga4-flattener':
        return hasGBQ && hasGA4;
      case 'marketing-attribution':
        return hasGBQ && hasGA4;
      case 'google-ads-cost-loader':
        return hasGBQ && hasGads;
      default:
        return false;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <span>Loading pipelines...</span>
      </div>
    );
  }

  return (
    <div className="pipeline-manager">
      {deleteError && (
        <div className="error-alert">
          {deleteError}
        </div>
      )}

      {/* Existing Pipelines */}
      <div className="existing-pipelines">
        <h3>Your Pipelines</h3>
        {pipelines.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🚀</div>
            <div className="empty-state-title">No pipelines yet</div>
            <div className="empty-state-text">Create your first pipeline below to get started.</div>
          </div>
        ) : (
          <div className="pipeline-list">
            {pipelines.map(pipeline => (
              <div key={pipeline.id} className="pipeline-item">
                <div className="pipeline-info">
                  <h4>{pipeline.name}</h4>
                  <p>Type: {pipeline.type}</p>
                  <p>Status: <span className={`status-${pipeline.status}`}>{pipeline.status}</span></p>
                  {pipeline.lastRun && (
                    <p>Last run: {new Date(pipeline.lastRun).toLocaleString()}</p>
                  )}
                  {pipeline.parameters?.ga4Project && (
                    <p>Project: {pipeline.parameters.ga4Project}</p>
                  )}
                  {pipeline.parameters?.ga4Dataset && (
                    <p>Dataset: {pipeline.parameters.ga4Dataset}</p>
                  )}
                  {pipeline.parameters?.ga4Table && pipeline.type !== 'ga4-sessionization' && (
                    <p>Table: {pipeline.parameters.ga4Table}</p>
                  )}
                </div>
                <div className="pipeline-actions">
                  <button
                    onClick={() => runPipeline(pipeline.id)}
                    disabled={pipeline.status === 'running'}
                    className="run-button"
                  >
                    {pipeline.status === 'running' ? 'Running...' : 'Run'}
                  </button>
                  <button
                    onClick={() => openEditPage(pipeline)}
                    className="edit-button"
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => deletePipeline(e, pipeline.id)}
                    className="delete-button"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available Pipeline Types */}
      <div className="pipeline-types">
        <h3>Available Pipeline Types</h3>
        <div className="pipeline-type-grid">
          <div className="pipeline-type-card">
            <h4>GA4 Sessionization</h4>
            <p>Creates user sessions from GA4 raw data</p>
            <div className="requirements">
              <strong>Requirements:</strong>
              <ul>
                {getPipelineRequirements('ga4-sessionization').map(req => (
                  <li key={req}>{req}</li>
                ))}
              </ul>
            </div>
            <button
              onClick={() => openCreatePage('ga4-sessionization')}
              disabled={!checkRequirementsMet('ga4-sessionization')}
            >
              Create Pipeline
            </button>
          </div>

          <div className="pipeline-type-card">
            <h4>GA4 Flattener</h4>
            <p>Flattens GA4 data for easier analysis</p>
            <div className="requirements">
              <strong>Requirements:</strong>
              <ul>
                {getPipelineRequirements('ga4-flattener').map(req => (
                  <li key={req}>{req}</li>
                ))}
              </ul>
            </div>
            <button
              onClick={() => openCreatePage('ga4-flattener')}
              disabled={!checkRequirementsMet('ga4-flattener')}
            >
              Create Pipeline
            </button>
          </div>

          <div className="pipeline-type-card">
            <h4>Marketing Attribution</h4>
            <p>Multi-touch attribution analysis</p>
            <div className="requirements">
              <strong>Requirements:</strong>
              <ul>
                {getPipelineRequirements('marketing-attribution').map(req => (
                  <li key={req}>{req}</li>
                ))}
              </ul>
            </div>
            <button
              onClick={() => openCreatePage('marketing-attribution')}
              disabled={!checkRequirementsMet('marketing-attribution')}
            >
              Create Pipeline
            </button>
          </div>

          <div className="pipeline-type-card">
            <h4>Google Ads Cost Loader</h4>
            <p>Loads Google Ads cost data into BigQuery</p>
            <div className="requirements">
              <strong>Requirements:</strong>
              <ul>
                {getPipelineRequirements('google-ads-cost-loader').map(req => (
                  <li key={req}>{req}</li>
                ))}
              </ul>
            </div>
            <button
              onClick={() => openCreatePage('google-ads-cost-loader')}
              disabled={!checkRequirementsMet('google-ads-cost-loader')}
            >
              Create Pipeline
            </button>
          </div>
        </div>
      </div>

      {/* Execution Date Modal */}
      {pendingPipelineId && (
        <ExecutionDateModal
          isOpen={showDateModal}
          onClose={() => {
            setShowDateModal(false);
            setPendingPipelineId(null);
          }}
          onConfirm={handleDateConfirm}
          pipelineName={pipelines.find(p => p.id === pendingPipelineId)?.name}
        />
      )}
      {/* Delete Confirmation Modal */}
      {confirmDeleteId && (
        <div className="delete-confirmation-modal">
          <div className="delete-confirmation-content">
            <h3 className="delete-confirmation-header">Confirm Deletion</h3>
            <p className="delete-confirmation-text">
              Are you sure you want to delete this pipeline? This action cannot be undone.
            </p>

            {deleteError && (
              <div className="error-alert" style={{ marginBottom: '20px' }}>
                {deleteError}
              </div>
            )}

            <div className="delete-confirmation-actions">
              <button
                onClick={cancelDelete}
                className="delete-confirmation-cancel"
              >
                Cancel
              </button>
              <button
                onClick={executeDelete}
                className="delete-confirmation-delete"
              >
                Delete Pipeline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineManager;